import React, { useState, useEffect, useMemo } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend 
} from 'recharts';

const IexPage = () => {
  // 1. STATE MANAGEMENT
  // Starts with today's date
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [marketData, setMarketData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null); // Holds the month's daily averages
  const [loading, setLoading] = useState(true);
  const [loadingMonth, setLoadingMonth] = useState(true);
  const [error, setError] = useState(null);
  
  // Visibility toggles for the main chart lines
  const [visible, setVisible] = useState({ dam: true, gdam: true, rtm: true });
  // Visibility toggles for the monthly overview graph lines
  const [visibleMonth, setVisibleMonth] = useState({ dam: true, gdam: true });

  // Dynamically compute the current month string (YYYY-MM) based on selectedDate
  const currentMonthStr = useMemo(() => {
    return selectedDate.substring(0, 7);
  }, [selectedDate]);

  // 2. FETCH LOGIC FOR INDIVIDUAL TARGET DATE
  const fetchLiveIex = async (date) => {
    setLoading(true);
    setError(null);
    try {
      // ✅ CRITICAL: Use backticks (``) and \${date} to send the date to your worker
      const WORKER_URL = `https://tight-flower-6a84.aditi-mumbai.workers.dev/?date=${date}`;
      
      const res = await fetch(WORKER_URL, {
        method: "GET",
        headers: { 
          "Accept": "application/json"
        }
      });

      if (!res.ok) {
        throw new Error(`Worker Error: ${res.status}. Check CORS settings in Worker.`);
      }
      
      const json = await res.json();
      
      if (json.success) {
        setMarketData(json);
      } else {
        throw new Error(json.error || "Scraper failed to find data for this date.");
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setError(err.message === "Failed to fetch" 
        ? "CORS Blocked or Worker Offline. Ensure 'OPTIONS' is handled in Worker." 
        : err.message);
    } finally {
      setLoading(false);
    }
  };

  // FETCH LOGIC FOR WHOLE MONTH DAILY AVERAGES (DAM & GDAM)
  const fetchMonthlyAverages = async (monthString) => {
    setLoadingMonth(true);
    try {
      const WORKER_URL = `https://tight-flower-6a84.aditi-mumbai.workers.dev/?month=${monthString}`;
      const res = await fetch(WORKER_URL, {
        method: "GET",
        headers: { "Accept": "application/json" }
      });
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          setMonthlyData(json);
        }
      }
    } catch (err) {
      console.error("Monthly Fetch Error:", err);
    } finally {
      setLoadingMonth(false);
    }
  };

  // Automatically fetch both datasets when the user selects a new date from the calendar
  useEffect(() => {
    fetchLiveIex(selectedDate);
  }, [selectedDate]);

  // Sync monthly data updates whenever the active month parameters change
  useEffect(() => {
    fetchMonthlyAverages(currentMonthStr);
  }, [currentMonthStr]);

  // 3. DATA PIVOT (Groups segments 1, 2, 3 into 96 rows and converts prices to ₹/kWh)
  const pivotedData = useMemo(() => {
    if (!marketData?.merged) return [];
    const blocks = {};

    marketData.merged.forEach(item => {
      if (!blocks[item.time]) {
        blocks[item.time] = { time: item.time, dam: null, gdam: null, rtm: null };
      }
      // Segment Mapping: 1 = DAM, 2 = GDAM, 3 = RTM
      // Prices are divided by 1000 and fixed to 2 decimal places (Rs/kWh)
      if (item.segment === 1) blocks[item.time].dam = parseFloat((item.price / 1000).toFixed(2));
      if (item.segment === 2) blocks[item.time].gdam = parseFloat((item.price / 1000).toFixed(2));
      if (item.segment === 3) blocks[item.time].rtm = parseFloat((item.price / 1000).toFixed(2));
    });

    // Sort blocks 00:00 to 23:45
    return Object.values(blocks).sort((a, b) => a.time.localeCompare(b.time));
  }, [marketData]);

  // Process whole month data for the summary graph container (Syntax Fix Applied Here)
  const transformedMonthlyGraphData = useMemo(() => {
    if (!monthlyData?.daily_metrics) return [];
    return monthlyData.daily_metrics.map(dayRow => {
      const dateParts = dayRow.date.split('-');
      const dayLabel = dateParts[2] || dayRow.date; // Fixed syntax error here
      return {
        day: dayLabel,
        fullDate: dayRow.date,
        dam: dayRow.DAM_avg_price !== null ? parseFloat((dayRow.DAM_avg_price / 1000).toFixed(2)) : null,
        gdam: dayRow.GDAM_avg_price !== null ? parseFloat((dayRow.GDAM_avg_price / 1000).toFixed(2)) : null
      };
    });
  }, [monthlyData]);

  const toggleVisibility = (key) => {
    setVisible(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleMonthlyVisibility = (key) => {
    setVisibleMonth(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-6">
      {/* HEADER & CALENDAR */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white p-4 rounded shadow gap-4 border-t-4 border-indigo-600">
        <div>
          <h2 className="font-bold text-lg text-slate-800 uppercase tracking-tight">IEX Prices</h2>
          <p className="text-[10px] text-slate-400 font-bold tracking-widest">
            {loading ? "FETCHING LIVE DATA..." : `DATE: ${selectedDate} | 96 BLOCKS`}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <label className="text-[10px] font-black text-slate-500 uppercase">Change Date:</label>
          <input 
            type="date" 
            value={selectedDate}
            max={new Date().toISOString().split('T')[0]} // Prevents future dates
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border rounded px-3 py-1 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button 
            onClick={() => { fetchLiveIex(selectedDate); fetchMonthlyAverages(currentMonthStr); }}
            className="bg-indigo-600 text-white px-4 py-1 rounded text-xs font-black uppercase hover:bg-indigo-700"
          >
            {loading ? "..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* ERROR MESSAGE DISPLAY */}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded text-xs font-bold border border-red-100 flex items-center gap-2">
          <span>⚠️</span> {error}
        </div>
      )}

      {/* ========================================================================= */}
      {/* ADDED SECTION: CURRENT MONTH DAILY AVERAGE VISUALIZATIONS (ABOVE GRAPH)   */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* MONTHLY SUMMARY GRAPH OVERVIEW */}
        <div className="bg-white p-4 rounded shadow border border-slate-100 lg:col-span-2">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-xs font-black uppercase text-slate-500 tracking-widest">Monthly Daily Average Trends</h3>
              <p className="text-[9px] text-slate-400 font-bold tracking-normal uppercase">Month Tracker: {currentMonthStr} (₹/kWh)</p>
            </div>
            {/* Monthly Toggles */}
            <div className="flex gap-3 text-[9px] font-bold uppercase">
               <label className="flex items-center gap-1 cursor-pointer text-blue-600">
                  <input type="checkbox" checked={visibleMonth.dam} onChange={() => toggleMonthlyVisibility('dam')} /> DAM
               </label>
               <label className="flex items-center gap-1 cursor-pointer text-teal-600">
                  <input type="checkbox" checked={visibleMonth.gdam} onChange={() => toggleMonthlyVisibility('gdam')} /> GDAM
               </label>
            </div>
          </div>

          <div className="h-[180px] w-full">
            {loadingMonth ? (
              <div className="h-full flex items-center justify-center text-slate-400 font-bold text-xs uppercase animate-pulse">Loading Month Summary...</div>
            ) : transformedMonthlyGraphData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={transformedMonthlyGraphData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f8fafc" />
                  <XAxis dataKey="day" fontSize={9} tick={{fill: '#94a3b8'}} />
                  <YAxis fontSize={9} tick={{fill: '#94a3b8'}} />
                  <Tooltip contentStyle={{fontSize: '11px', borderRadius: '6px'}} formatter={(value) => [`₹${value} / kWh`]} />
                  {visibleMonth.dam && <Line type="monotone" dataKey="dam" name="DAM Avg" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} connectNulls={true} />}
                  {visibleMonth.gdam && <Line type="monotone" dataKey="gdam" name="GDAM Avg" stroke="#0d9488" strokeWidth={2} dot={{ r: 2 }} connectNulls={true} />}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-300 text-xs">No monthly data available</div>
            )}
          </div>
        </div>

        {/* MONTHLY SUMMARY TABULATED OVERVIEW */}
        <div className="bg-white rounded shadow border border-slate-100 flex flex-col h-[236px]">
          <div className="bg-slate-50 p-3 border-b border-slate-100 sticky top-0 z-10">
            <h3 className="text-xs font-black uppercase text-slate-500 tracking-widest">Monthly Price Data Table</h3>
          </div>
          <div className="overflow-y-auto flex-1 max-h-[190px]">
            <table className="w-full text-left text-[11px] border-collapse">
              <thead className="bg-slate-100 text-slate-600 sticky top-0 uppercase text-[8px] font-bold border-b border-slate-200 z-10">
                <tr>
                  <th className="p-2">Date</th>
                  <th className="p-2 text-center text-blue-600">DAM (₹/kWh)</th>
                  <th className="p-2 text-center text-teal-600">GDAM (₹/kWh)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingMonth ? (
                  <tr>
                    <td colSpan="3" className="p-10 text-center text-slate-400 font-bold uppercase animate-pulse">
                      Syncing...
                    </td>
                  </tr>
                ) : monthlyData?.daily_metrics && monthlyData.daily_metrics.length > 0 ? (
                  monthlyData.daily_metrics.map((row, index) => {
                    const parsedDam = row.DAM_avg_price !== null ? (row.DAM_avg_price / 1000).toFixed(2) : "-";
                    const parsedGdam = row.GDAM_avg_price !== null ? (row.GDAM_avg_price / 1000).toFixed(2) : "-";
                    return (
                      <tr key={index} className="hover:bg-slate-50 transition-colors">
                        <td className="p-2 font-medium text-slate-500">{row.date}</td>
                        <td className="p-2 text-center font-bold text-blue-600">₹{parsedDam}</td>
                        <td className="p-2 text-center font-bold text-teal-600">₹{parsedGdam}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="p-4 text-center text-slate-400">
                      No data parsed.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* ========================================================================= */}

      {/* TREND CHART */}
      <div className="bg-white p-6 rounded shadow border border-slate-100">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xs font-black uppercase text-slate-500 tracking-widest">Price Trend (₹/kWh)</h3>
          
          {/* Legend Checkboxes to hide/unhide trends */}
          <div className="flex gap-4 text-[10px] font-bold uppercase">
             <label className="flex items-center gap-1 cursor-pointer text-blue-600">
                <input type="checkbox" checked={visible.dam} onChange={() => toggleVisibility('dam')} /> DAM
             </label>
             <label className="flex items-center gap-1 cursor-pointer text-teal-600">
                <input type="checkbox" checked={visible.gdam} onChange={() => toggleVisibility('gdam')} /> GDAM
             </label>
             <label className="flex items-center gap-1 cursor-pointer text-rose-600">
                <input type="checkbox" checked={visible.rtm} onChange={() => toggleVisibility('rtm')} /> RTM
             </label>
          </div>
        </div>

        <div className="h-[350px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pivotedData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="time" fontSize={9} interval={7} tick={{fill: '#94a3b8'}} />
              <YAxis fontSize={10} tick={{fill: '#94a3b8'}} />
              <Tooltip 
                contentStyle={{fontSize: '12px', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}
                formatter={(value) => [`₹${value} / kWh`]}
              />
              <Legend verticalAlign="top" height={36} iconType="circle" />
              
              {visible.dam && <Line type="monotone" dataKey="dam" name="DAM" stroke="#2563eb" strokeWidth={2.5} dot={false} connectNulls={true} />}
              {visible.gdam && <Line type="monotone" dataKey="gdam" name="GDAM" stroke="#0d9488" strokeWidth={2.5} dot={false} connectNulls={true} />}
              {visible.rtm && <Line type="monotone" dataKey="rtm" name="RTM" stroke="#e11d48" strokeWidth={2.5} dot={false} connectNulls={true} />}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 96 BLOCKS TABLE */}
      <div className="bg-white rounded shadow overflow-hidden border border-slate-100">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-slate-800 text-white sticky top-0 uppercase text-[9px] z-20">
              <tr className="divide-x divide-slate-700">
                <th className="p-3 w-32">Time Block</th>
                <th className="p-3 text-center bg-blue-900/50">DAM (₹/kWh)</th>
                <th className="p-3 text-center bg-teal-800/50">GDAM (₹/kWh)</th>
                <th className="p-3 text-center bg-rose-900/50">RTM (₹/kWh)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="4" className="p-20 text-center text-slate-400 font-bold uppercase animate-pulse">
                    Syncing with IEX Cloud...
                  </td>
                </tr>
              ) : pivotedData.length > 0 ? (
                pivotedData.map((row, i) => (
                  <tr key={i} className="hover:bg-indigo-50/50 divide-x divide-slate-50 transition-colors">
                    <td className="p-2 font-bold text-slate-500 bg-slate-50/50">{row.time}</td>
                    <td className={`p-2 text-center font-bold ${visible.dam ? 'text-blue-600' : 'text-slate-200'}`}>
                      {row.dam ? `₹${row.dam.toFixed(2)}` : "-"}
                    </td>
                    <td className={`p-2 text-center font-bold ${visible.gdam ? 'text-teal-600' : 'text-slate-200'}`}>
                      {row.gdam ? `₹${row.gdam.toFixed(2)}` : "-"}
                    </td>
                    <td className={`p-2 text-center font-bold ${visible.rtm ? 'text-rose-600' : 'text-slate-200'}`}>
                      {row.rtm ? `₹${row.rtm.toFixed(2)}` : "-"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="p-20 text-center text-slate-400">
                    No data found for this date.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default IexPage;

